import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/data/homeContent";
import { ArrowLeft, Plus, Trash2, Loader2, Globe2, ChevronDown, ChevronUp, Save } from "lucide-react";

export default function Countries() {
  const [groups, setGroups] = useState([]); // [{country, schools:[{id,school_name,slug}]}]
  const [extraCountries, setExtraCountries] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState("");
  const [savingOrder, setSavingOrder] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/countries"), api.get("/countries")])
      .then(([a, b]) => { setExtraCountries(a.data); setGroups(b.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const countName = (country) => (groups.find((g) => g.country === country)?.schools.length) || 0;

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/countries", { name: name.trim() });
      toast.success(`Country "${name.trim()}" added`);
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Remove country "${c}"?`)) return;
    try {
      await api.delete(`/admin/countries/${encodeURIComponent(c)}`);
      toast.success("Country removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove");
    }
  };

  const moveSchool = (country, idx, dir) => {
    setGroups((gs) => gs.map((g) => {
      if (g.country !== country) return g;
      const j = idx + dir;
      if (j < 0 || j >= g.schools.length) return g;
      const schools = [...g.schools];
      [schools[idx], schools[j]] = [schools[j], schools[idx]];
      return { ...g, schools };
    }));
  };

  const saveOrder = async (country) => {
    const g = groups.find((x) => x.country === country);
    if (!g) return;
    setSavingOrder(country);
    try {
      await api.put(`/admin/countries/${encodeURIComponent(country)}/order`, { ids: g.schools.map((s) => s.id) });
      toast.success(`Order saved for ${country}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save order");
    } finally {
      setSavingOrder("");
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6]" data-testid="admin-countries">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#800020]" data-testid="countries-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </Link>
          <img src={LOGO_URL} alt="BSD" className="h-8" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="font-serif text-3xl text-slate-900 mb-1">Manage Countries</h1>
        <p className="text-slate-500 text-sm mb-6">Add or remove countries, and reorder the schools/colleges shown under each country in the menu.</p>

        <form onSubmit={add} className="flex gap-3 mb-8">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New country name" className="flex-1 border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-[#800020]" data-testid="country-name-input" />
          <button type="submit" disabled={adding} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors disabled:opacity-60" data-testid="country-add-btn">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </form>

        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
        ) : (
          <div className="space-y-3">
            {extraCountries.map((c) => {
              const g = groups.find((x) => x.country === c) || { country: c, schools: [] };
              const isOpen = open === c;
              return (
                <div key={c} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`country-row-${c}`}>
                  <div className="flex items-center justify-between px-5 py-3">
                    <button onClick={() => setOpen(isOpen ? "" : c)} className="inline-flex items-center gap-2.5 text-slate-800 font-medium" data-testid={`country-expand-${c}`}>
                      <Globe2 className="h-4 w-4 text-[#800020]" /> {c}
                      <span className="text-xs text-slate-400">{countName(c)} school{countName(c) === 1 ? "" : "s"}</span>
                      {g.schools.length > 0 && (isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />)}
                    </button>
                    <button onClick={() => del(c)} disabled={countName(c) > 0} title={countName(c) > 0 ? "Country in use by listings" : "Remove"} className="p-2 rounded hover:bg-red-50 text-red-500 disabled:text-slate-300 disabled:hover:bg-transparent" data-testid={`country-delete-${c}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {isOpen && g.schools.length > 0 && (
                    <div className="border-t border-slate-100 px-5 py-4" data-testid={`country-schools-${c}`}>
                      <ul className="space-y-2">
                        {g.schools.map((s, idx) => (
                          <li key={s.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                            <span className="text-sm text-slate-700">{idx + 1}. {s.school_name}</span>
                            <div className="flex items-center gap-1">
                              <button onClick={() => moveSchool(c, idx, -1)} disabled={idx === 0} className="p-1.5 rounded hover:bg-white text-slate-500 disabled:text-slate-300" data-testid={`school-up-${s.slug}`}><ChevronUp className="h-4 w-4" /></button>
                              <button onClick={() => moveSchool(c, idx, 1)} disabled={idx === g.schools.length - 1} className="p-1.5 rounded hover:bg-white text-slate-500 disabled:text-slate-300" data-testid={`school-down-${s.slug}`}><ChevronDown className="h-4 w-4" /></button>
                            </div>
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => saveOrder(c)} disabled={savingOrder === c} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#800020] hover:bg-[#660019] text-white text-sm font-medium transition-colors disabled:opacity-60" data-testid={`country-save-order-${c}`}>
                        {savingOrder === c ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Order
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
